import { Link } from "react-router-dom";
import { ArrowRight, MapPin, FolderCheck, Send } from "lucide-react";
import Nav from "@/components/marketing/Nav";
import Footer from "@/components/marketing/Footer";
import { Reveal, RevealGroup, Item } from "@/components/Reveal";

const steps = [
  { Icon: MapPin, title: "1 · Choose your route", body: "Select your destination and visa type. Holavisto loads a structured, source-linked requirement set for exactly that route, versioned and dated so you always know how current it is." },
  { Icon: FolderCheck, title: "2 · Build and check your case", body: "Add your applicant details and upload your documents. We keep originals private and unchanged, then run deterministic checks that compare names, dates and financial periods across your evidence, surfacing what needs attention in plain language." },
  { Icon: Send, title: "3 · Get ready to submit", body: "Resolve findings, optionally request a human review, and prepare an organized dossier. When everything is complete, continue confidently through the official government process." },
];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-softcream">
      <Nav />
      <section className="mx-auto max-w-page px-5 py-16 sm:px-8 sm:py-20 md:py-28 lg:px-12">
        <Reveal>
          <p className="mb-4 text-sm font-bold uppercase tracking-[0.2em] text-coral sm:text-base">How it works</p>
          <h1 className="max-w-3xl font-serif text-4xl leading-tight tracking-tight text-heading sm:text-5xl md:text-6xl">A calmer path from scattered documents to submission ready.</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">Holavisto turns a fragmented process into one guided workflow. Here is what happens at each step.</p>
        </Reveal>
        <RevealGroup className="mt-12 space-y-6">
          {steps.map((s, i) => (
            <Item key={s.title} className="flex flex-col gap-5 rounded-md border border-hborder bg-white p-6 transition- duration-300 sm:p-7 md:flex-row md:items-start" data-testid={`howitworks-step-${i + 1}`}>
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-cream"><s.Icon className="text-coral" size={26} /></span>
              <div>
                <h2 className="font-serif text-2xl text-heading">{s.title}</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-muted">{s.body}</p>
              </div>
            </Item>
          ))}
        </RevealGroup>
        <Reveal className="mt-12 rounded-md bg-navy p-6 text-white sm:p-8">
          <h2 className="font-serif text-2xl sm:text-3xl">What Holavisto never does</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {["Guarantee or predict a visa outcome", "Decide applications or act as a government", "Alter your original documents", "Invent requirements, sources or appointments"].map((x) => (
              <p key={x} className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-[15px] text-white/85">{x}</p>
            ))}
          </div>
        </Reveal>
        <div className="mt-10 text-center">
          <Link to="/register" className="inline-flex items-center gap-2 rounded bg-brandyellow px-8 py-3.5 text-sm font-semibold text-heading transition-all duration-300 " data-testid="hiw-start-btn">
            Start your application <ArrowRight size={16} />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
